import classNames from 'classnames'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import {SimpleBlueButton} from '../../components/core/button'
import {PageSubTitle, PageTitle, ResponsivePageTitle} from '../../components/core/title'
import Tooltip from '../../components/core/tooltip'
import Footer from '../../components/layouts/footer'
import LinearProgressBar from '../../components/pages/lineProgressBar'
import plans from '../../data/plans.json'
import usageLimits from '../../data/usageLimits.json'
import {
    ClosedIncidentManagementIcon,
    NewInfoIcon,
    StatuspageIcon,
    UptimeMonitoringIcon
} from '../../icons'
import {routes} from '../../routes'
import {getAllLimitUsages} from '../../stores/auth'
import {colors} from '../../utils/colors'
import TextHelper from '../../utils/text'
import './index.scss'
import text from './text.json'
import renderSkeletonLoaders from '../../utils/loader'

const SECTION_ICONS = {
    monitoring: <UptimeMonitoringIcon stroke={colors.colorBlue} />,
    statuspage: <StatuspageIcon stroke={colors.colorBlue} />,
    incident_management:
        <ClosedIncidentManagementIcon
            strokeColor={colors.colorBlue}
            strokeWidth={0.397222}
            height={26}
        />

}

const sections = Object.values(usageLimits.limit_modules).map(data => ({...data, icon: SECTION_ICONS?.[data?.module]}))

const Usage = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const isDarkMode = useSelector((state) => state.sidebar.isDarkMode)
    const {organisationLimitUsages, isUsageLoading} = useSelector((state) => state.auth)
    const currentPlan = useMemo(() => localStorage.getItem('plan'), [])
    const usageLimitValues = useMemo(() => Object.values(usageLimits.limit_keys), [])
    const [isFirstLoad, setIsFirstLoad] = useState(true)

    const sortedSections = useMemo(() => {
        return sections.sort((a, b) => {
            if (a.hash === location.hash) return -1
            if (b.hash === location.hash) return 1
            return 0
        })
    }, [location.hash])

    const section = useMemo(
        () => sections.find((section) => section.hash === location.hash),
        [location.hash]
    )

    const handleSeePlansPage = useCallback(() => {
        navigate(`${routes.plans.path}${section?.hash}`)
    }, [navigate, section])

    const generateUsageLimits = useCallback(
        (section) => {
            const usageData = organisationLimitUsages?.[section?.module]

            return (
                <div className={classNames('usage-limits-module', {
                    hidden: !(usageData || isUsageLoading || isFirstLoad)
                })} key={section.module}>
                    <span className="usage-limits-module-header">
                        {isUsageLoading || isFirstLoad ? renderSkeletonLoaders(1) :
                            <>
                                <span>{section.icon}</span>
                                <h3 className="usage-limits-title">{section?.title}</h3>
                            </>}
                    </span>
                    <span className="usage-limits-items">
                        {isUsageLoading || isFirstLoad ? renderSkeletonLoaders(12, 'select-skeleton') :
                            usageData?.map((singleUsageLimit) => {
                                const limitUsageInfoText = usageLimitValues.find(
                                    (usageLimit) =>
                                        usageLimit?.limit_key === singleUsageLimit.key
                                )?.info_text

                                return (
                                    <span className="usage-limit-item" key={singleUsageLimit.key}>
                                        <span className="usage-limit-item-title">
                                            <span>{singleUsageLimit?.title}</span>
                                            <Tooltip
                                                placement="left"
                                                trigger="mouseenter"
                                                content={limitUsageInfoText}
                                            >
                                                <NewInfoIcon />
                                            </Tooltip>
                                        </span>
                                        <span className="usage-limit-progress-label">
                                            <LinearProgressBar
                                                value={singleUsageLimit?.usage}
                                                max={singleUsageLimit?.value}
                                            />
                                        </span>
                                    </span>
                                )
                            })}
                    </span>
                </div>
            )
        },
        [organisationLimitUsages, usageLimitValues, isFirstLoad, isUsageLoading]
    )

    useEffect(() => {
        dispatch(getAllLimitUsages())
        setIsFirstLoad(false)
    }, [dispatch])

    return (
        <main
            className={classNames('main usage', {
                dark: isDarkMode
            })}
        >
            <div>
                <ResponsivePageTitle />
                <div className="content-title">
                    <div className="left-side">
                        <div>
                            <PageSubTitle>
                                <p
                                    className={classNames('section', {
                                        dark: isDarkMode
                                    })}
                                >
                                    {section?.title}
                                </p>
                            </PageSubTitle>
                            <PageTitle>
                                <h1
                                    className={classNames('page-title', {
                                        dark: isDarkMode
                                    })}
                                >
                                    {text.title}
                                </h1>
                            </PageTitle>
                        </div>
                    </div>
                    <SimpleBlueButton id="see-plans-button" onClick={handleSeePlansPage}>
                        {text.see_plans}
                    </SimpleBlueButton>
                </div>
                <span
                    className={classNames('usage-text-container', {
                        dark: isDarkMode
                    })}
                >
                    <span
                        dangerouslySetInnerHTML={{
                            __html: TextHelper.replaceAllChars(
                                text.to_increase_your_limits_text,
                                ':upgrade_text',
                                currentPlan === plans.pro.key
                                    ? text.contact_mail_text
                                    : text.upgrade_plan_text
                            )
                        }}
                    />
                </span>
                <span className="usage-limits-container">
                    {sortedSections.map(generateUsageLimits)}
                </span>
            </div>
            <Footer />
        </main>
    )
}

export default Usage
